import React, { createContext, FC, PropsWithChildren, useContext, useState } from 'react';
import {
  UnifyLocationDetails,
  UnifyLocationNotes,
  UnifyLocationRow,
} from './components/unify-locations-table/unify-locations-table.types';
import { MetricResult } from '../handoff-metrics/handoff-metrics.types';
import { HandoffMetric } from 'components/onboarding/handoff-hub/handoff-hub.types';
import { useUnifyLocationSelection } from './hooks/use-unify-location-selection';
import { useUnifyDraftRehydration } from './hooks/use-unify-draft-rehydration';
import { useUnifyHandoffSubmit } from './hooks/use-unify-handoff-submit';

export type UnifyWizardStep = 'select-locations' | 'evaluation' | 'snapshot';

export interface UnifyHandoffContextValue {
  currentStep: UnifyWizardStep;
  isLoadingDraft: boolean;
  locations: UnifyLocationRow[];
  selectedLocationIds: string[];
  selectedLocations: UnifyLocationRow[];
  locationDetailsMap: Record<string, UnifyLocationDetails>;
  snapshotMetricsByLocation: Record<string, HandoffMetric[]>;
  groupId?: string;
  handoffIdMap: Record<string, string>;
  maxSelections: number;
  showValidationErrors: boolean;
  canProceedToEvaluation: boolean;
  hasMinSelections: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  handleSelectionChange: (selectedIds: string[]) => void;
  handlePocChange: (locationId: string, pocEmail: string) => void;
  handleNotesChange: (locationId: string, notes: UnifyLocationNotes) => void;
  goToNextStep: () => Promise<boolean> | boolean;
  onCancel: () => void;
  onSaveAndClose: () => Promise<void> | void;
  onSubmit: (resultsByLocation: Record<string, MetricResult[]>) => void;
}

export interface UnifyHandoffProviderProps {
  locations: UnifyLocationRow[];
  initialStep?: UnifyWizardStep;
  initialSelectedLocationIds?: string[];
  initialLocationDetailsMap?: Record<string, UnifyLocationDetails>;
  initialGroupId?: string;
  initialHandoffIdMap?: Record<string, string>;
  maxSelections?: number;
  minSelections?: number;
  isSaving?: boolean;
  isSubmitting?: boolean;
  onCancel?: () => void;
  onSaveAndClose?: (locationDetailsMap?: Record<string, UnifyLocationDetails>) => void;
  onSubmit?: (resultsByLocation: Record<string, MetricResult[]>) => void;
}

export const UnifyHandoffContext = createContext<UnifyHandoffContextValue>({
  currentStep: 'select-locations',
  isLoadingDraft: false,
  locations: [],
  selectedLocationIds: [],
  selectedLocations: [],
  locationDetailsMap: {},
  snapshotMetricsByLocation: {},
  groupId: undefined,
  handoffIdMap: {},
  maxSelections: 5,
  showValidationErrors: false,
  canProceedToEvaluation: false,
  hasMinSelections: false,
  isSaving: false,
  isSubmitting: false,
  handleSelectionChange: () => {},
  handlePocChange: () => {},
  handleNotesChange: () => {},
  goToNextStep: () => false,
  onCancel: () => {},
  onSaveAndClose: () => {},
  onSubmit: () => {},
});

export const UnifyHandoffProvider: FC<PropsWithChildren<UnifyHandoffProviderProps>> = ({
  children,
  locations = [],
  initialStep = 'select-locations',
  initialSelectedLocationIds = [],
  initialLocationDetailsMap = {},
  initialGroupId,
  initialHandoffIdMap = {},
  maxSelections = 5,
  minSelections = 1,
  isSaving = false,
  isSubmitting = false,
  onCancel = () => {},
  onSaveAndClose = () => {},
  onSubmit = () => {},
}) => {
  const [currentStep, setCurrentStep] = useState<UnifyWizardStep>(initialStep);
  const [showValidationErrors, setShowValidationErrors] = useState<boolean>(false);

  // Hook 1: Location selection and details state
  const {
    selectedLocationIds,
    setSelectedLocationIds,
    locationDetailsMap,
    setLocationDetailsMap,
    selectedLocations,
    hasMinSelections,
    canProceedToEvaluation,
    handleSelectionChange,
    handlePocChange,
    handleNotesChange,
  } = useUnifyLocationSelection({
    locations,
    initialSelectedLocationIds,
    initialLocationDetailsMap,
    maxSelections,
    minSelections,
  });

  // Hook 2: Draft snapshot rehydration (POC, notes, metrics)
  const {
    groupId,
    setGroupId,
    handoffIdMap,
    setHandoffIdMap,
    snapshotMetricsByLocation,
    isLoadingDraft,
  } = useUnifyDraftRehydration({
    initialGroupId,
    initialHandoffIdMap,
    onRehydrateSelection: setSelectedLocationIds,
    onRehydrateDetails: setLocationDetailsMap,
  });

  // Hook 3: Draft save, step navigation, and batch submission
  const {
    isSaving: effectiveIsSaving,
    isSubmitting: effectiveIsSubmitting,
    handleSaveAndClose,
    goToNextStep,
    handleSubmit,
  } = useUnifyHandoffSubmit({
    currentStep,
    setCurrentStep,
    selectedLocations,
    locationDetailsMap,
    groupId,
    setGroupId,
    handoffIdMap,
    setHandoffIdMap,
    canProceedToEvaluation,
    setShowValidationErrors,
    isSavingProp: isSaving,
    isSubmittingProp: isSubmitting,
    onSaveAndClose,
    onSubmit,
  });

  return (
    <UnifyHandoffContext.Provider
      value={{
        currentStep,
        isLoadingDraft,
        locations,
        selectedLocationIds,
        selectedLocations,
        locationDetailsMap,
        snapshotMetricsByLocation,
        groupId,
        handoffIdMap,
        maxSelections,
        showValidationErrors,
        canProceedToEvaluation,
        hasMinSelections,
        isSaving: effectiveIsSaving,
        isSubmitting: effectiveIsSubmitting,
        handleSelectionChange,
        handlePocChange,
        handleNotesChange,
        goToNextStep,
        onCancel,
        onSaveAndClose: handleSaveAndClose,
        onSubmit: handleSubmit,
      }}
    >
      {children}
    </UnifyHandoffContext.Provider>
  );
};

export const useUnifyHandoffContext = (): UnifyHandoffContextValue => {
  return useContext(UnifyHandoffContext);
};
